import animation, { shake } from '@helpers/animation';
import brand from '@helpers/brand';
import IconDownBlack from '@img/icons/icon-down-black.svg?url';
import Tick from '@img/icons/tick-red.svg?url';
import { baseControl, baseControlUnderline } from '@stories/Elements/Forms/Fields/Form.styles';
import styled, { css } from 'styled-components';
import { LabelUnderline } from '../Textbox/Textbox.styles';
import { SelectTheme } from './SelectUnderline';

export const SelectInput = styled.select`
  ${baseControl};
  padding: 0 30px 0 10px;
  background: url(${IconDownBlack}) no-repeat right 10px center;
  background-size: 9px 6px;
  background-color: ${brand.white};

  :invalid {
    color: ${brand.grey[45]};
    option {
      color: black;
    }
  }
`;

const SelectUnderlineContainer = styled.div<{
  activeState: boolean;
  disabled?: boolean;
  selectTheme?: SelectTheme;
  placeholderColour?: boolean;
}>`
  ${baseControlUnderline};
  position: relative;
  cursor: pointer;

  /* align active value */
  display: flex;
  align-items: center;

  &:not([type='date']):after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 6px 0 6px;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
  }

  &[type='date'] {
    cursor: pointer;
    padding-right: 16px !important;
    color-scheme: dark;

    // Hide default placeholder text so we can show DatePlaceHolder
    &:not(:valid) {
      color: transparent;
    }
  }

  ${({ selectTheme, activeState }) => {
    switch (selectTheme) {
      default:
      case SelectTheme.Red: {
        return css`
          border: ${activeState ? `2px solid ${brand.white}` : '2px solid transparent'};
          border-bottom: 2px solid ${brand.white};
          background-color: ${brand.red.dark};
          color: ${brand.white};

          &::after {
            border-color: ${brand.white} transparent transparent transparent;
          }

          &:focus {
            border-color: ${brand.white};
          }
        `;
      }

      case SelectTheme.Red_Base: {
        return css`
          border: none;
          border-bottom: 2px solid ${brand.white};
          background-color: ${brand.red.base};
          color: ${brand.white};

          &::after {
            border-color: ${brand.white} transparent transparent transparent;
          }

          &:focus {
            border-color: ${brand.white};
          }

          ${() => SelectUnderlineDropdown} {
            background-color: ${brand.white};
          }

          ${() => SelectUnderlineItem} {
            border-left: 1px solid transparent;

            &:hover {
              background-color: ${brand.grey[95]};
              border-color: #e10422;
            }
          }
        `;
      }

      case SelectTheme.White: {
        return css`
          border-bottom: 2px solid ${brand.grey[25]};
          background-color: ${brand.white};
          color: ${brand.black};

          &::after {
            border-color: ${brand.black} transparent transparent transparent;
          }

          &:focus {
            border-bottom: 2px solid ${brand.grey[25]};
          }
        `;
      }

      case SelectTheme.Black: {
        return css`
          border-bottom: 2px solid ${brand.grey[25]};
          color: ${brand.black};

          &::after {
            border-color: ${brand.black} transparent transparent transparent;
          }

          &:focus {
            border-bottom: 2px solid ${brand.grey[25]};
          }
        `;
      }

      case SelectTheme.White_Text: {
        return css`
          border-bottom: 2px solid ${brand.white};
          color: ${brand.white};

          &::after {
            border-color: ${brand.white} transparent transparent transparent;
          }

          &:focus {
            border-bottom: 2px solid ${brand.white};
          }
        `;
      }
    }
  }};

  ${({ placeholderColour }) =>
    placeholderColour
      ? css`
          color: ${brand.grey[55]};
        `
      : null}

  ${({ activeState }) =>
    activeState
      ? css`
          z-index: 20;
        `
      : null}

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: default;
          opacity: 0.6;
        `
      : null}
`;

const Title = styled.small<{ selectTheme?: SelectTheme }>`
  font-size: 14px;
  padding: 0 20px;
  color: ${brand.white};

  ${({ selectTheme }) =>
    selectTheme === SelectTheme.Black
      ? css`
          color: ${brand.grey[45]};
        `
      : null}
`;

const ActiveSelect = styled.div`
  padding-right: 12px;
`;

const SelectUnderlineDropdown = styled.div<{ activeState: boolean; selectTheme?: SelectTheme }>`
  display: none;
  background-color: ${brand.grey[95]};
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 2;
  max-height: 300px;
  overflow-y: auto;

  ${({ selectTheme }) =>
    selectTheme === SelectTheme.Black
      ? css`
          top: calc(100% + 2px);
          background-color: ${brand.white};
          box-shadow: 0px 3px 6px rgb(0 0 0 / 25%);
        `
      : null}

  ${({ activeState }) =>
    activeState
      ? css`
          display: block;
        `
      : null}
`;

const SelectUnderlineItem = styled.div`
  color: ${brand.black};
  position: relative;
  padding: 10px 20px 9px;

  &:hover {
    background-color: ${brand.grey[90]};
  }
`;

const FlagItem = styled.div`
  display: flex;
  align-items: center;
`;

const FlagImage = styled.svg`
  margin-left: 20px;
  width: 36px;
  height: 36px;
`;

const MultiSelectUnderlineItem = styled.div<{ isActive: boolean }>`
  color: ${brand.black};
  position: relative;
  padding: 10px 20px 9px;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 16px;
    background: url(${Tick}) no-repeat center;
    width: 16px;
    height: 100%;
    opacity: 0;
    transform: scale(0);
    transform-origin: left center;
    transition: opacity 150ms ${animation.easeInOutCubic}, transform 150ms ${animation.easeInOutCubic};
  }

  ${({ isActive }) =>
    isActive
      ? css`
          color: ${brand.red.base};

          &:after {
            transform: scale(1);
            opacity: 1;
          }
        `
      : null}
`;

const Wrapper = styled.div<{ myTheme?: SelectTheme; hasError: boolean }>`
  ${({ hasError }) =>
    hasError &&
    css`
      animation: ${shake} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    `}

  ${({ myTheme, hasError }) => {
    if (!hasError) {
      return;
    }

    switch (myTheme) {
      case SelectTheme.White_Text:
        return css`
          ${SelectUnderlineContainer} {
            border-bottom: 2px solid ${brand.red.base};
          }

          ${LabelUnderline} {
            color: ${brand.red.base};
          }
        `;

      default:
        break;
    }
  }}
`;

const DatePlaceHolder = styled(Title)`
  position: absolute;
  top: 0;
  height: 45px;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 16px;
  width: 100%;
  cursor: pointer;
`;

const BaseSelectUnderlineContainer = styled.select<{
  disabled?: boolean;
  selectTheme?: SelectTheme;
  placeholderColour?: boolean;
}>`
  ${baseControlUnderline};
  position: relative;
  cursor: pointer;

  /* align active value */
  display: flex;
  align-items: center;

  &:not([type='date']):after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 6px 0 6px;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
  }

  &[type='date'] {
    cursor: pointer;
    padding-right: 16px !important;
    color-scheme: dark;

    // Hide default placeholder text so we can show DatePlaceHolder
    &:not(:valid) {
      color: transparent;
    }
  }

  ${({ selectTheme }) => {
    switch (selectTheme) {
      default:
      case SelectTheme.Red: {
        return css`
          border: 2px solid ${brand.white};
          border-bottom: 2px solid ${brand.white};
          background-color: ${brand.red.dark};
          color: ${brand.white};

          &::after {
            border-color: ${brand.white} transparent transparent transparent;
          }

          &:focus {
            border-color: ${brand.white};
          }
        `;
      }

      case SelectTheme.Red_Base: {
        return css`
          border: none;
          border-bottom: 2px solid ${brand.white};
          background-color: ${brand.red.base};
          color: ${brand.white};

          &::after {
            border-color: ${brand.white} transparent transparent transparent;
          }

          &:focus {
            border-color: ${brand.white};
          }

          ${() => SelectUnderlineDropdown} {
            background-color: ${brand.white};
          }

          ${() => SelectUnderlineItem} {
            border-left: 1px solid transparent;

            &:hover {
              background-color: ${brand.grey[95]};
              border-color: #e10422;
            }
          }
        `;
      }

      case SelectTheme.White: {
        return css`
          border-bottom: 2px solid ${brand.grey[25]};
          background-color: ${brand.white};
          color: ${brand.black};

          &::after {
            border-color: ${brand.black} transparent transparent transparent;
          }

          &:focus {
            border-bottom: 2px solid ${brand.grey[25]};
          }
        `;
      }

      case SelectTheme.Black: {
        return css`
          border-bottom: 2px solid ${brand.grey[25]};
          color: ${brand.black};

          &::after {
            border-color: ${brand.black} transparent transparent transparent;
          }

          &:focus {
            border-bottom: 2px solid ${brand.grey[25]};
          }
        `;
      }

      case SelectTheme.White_Text: {
        return css`
          border-bottom: 2px solid ${brand.white};
          color: ${brand.white};

          &::after {
            border-color: ${brand.white} transparent transparent transparent;
          }

          &:focus {
            border-bottom: 2px solid ${brand.white};
          }
        `;
      }
    }
  }};

  ${({ placeholderColour }) =>
    placeholderColour
      ? css`
          color: ${brand.grey[55]};
        `
      : null}

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: default;
        `
      : null}
`;

const SearchInput = styled.input`
  width: 100%;
  border-radius: 0;
  border: 0;
  border-bottom: 1px solid #e4e4e4;
  margin-bottom: 5px;
  padding: 5px 15px;

  &::placeholder {
    color: #222;
  }
`;

export const SelectUnderlineStyles = {
  Wrapper,
  SelectUnderlineContainer,
  BaseSelectUnderlineContainer,
  Title,
  ActiveSelect,
  SelectUnderlineDropdown,
  SelectUnderlineItem,
  MultiSelectUnderlineItem,
  FlagItem,
  FlagImage,
  DatePlaceHolder,
  SearchInput,
};
